import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { PhoneInput } from '@kioskgaming/ui';
import CashierLayout from '../components/CashierLayout';
import {
  cashierCreatePlayer,
  cashierDepositCredits,
  cashierFetchPlayers,
  cashierGetShift,
  cashierListTransactions,
  cashierRedeemCredits,
  CashierShift,
  CashierTransaction,
} from '../services/cashierApi';
import { formatCurrency } from '../utils/denominations';
import { canJoinShift, isShiftViewable, shiftNotViewableMessage } from '../utils/shiftAccess';
import {
  formatTxType,
  getTxStatus,
  txAmountClass,
  txStatusBadgeClass,
  txStatusLabel,
} from '../utils/transactionDisplay';

type PlayerRow = {
  id: string;
  phone?: string | null;
  username?: string | null;
  balance?: number;
};

export default function ActiveShiftPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [shift, setShift] = useState<CashierShift | null>(null);
  const [transactions, setTransactions] = useState<CashierTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [players, setPlayers] = useState<PlayerRow[]>([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [amount, setAmount] = useState<Record<string, string>>({});

  const [createOpen, setCreateOpen] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [creating, setCreating] = useState(false);

  const loadShift = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [shiftRes, txRes] = await Promise.all([
        cashierGetShift(id),
        cashierListTransactions(id),
      ]);
      if (!shiftRes.success || !shiftRes.data?.shift) {
        toast.error(shiftRes.message || shiftRes.code || 'Shift not found');
        setShift(null);
        return;
      }
      const loadedShift = shiftRes.data.shift;
      if (!isShiftViewable(loadedShift.status)) {
        toast.error(shiftNotViewableMessage(loadedShift.status));
        setShift(null);
        navigate('/', { replace: true });
        return;
      }
      setShift(loadedShift);
      setTransactions(txRes.success ? txRes.data || [] : shiftRes.data.transactions || []);
    } finally {
      setLoading(false);
    }
  };

  const loadPlayers = async () => {
    setLoadingPlayers(true);
    try {
      const res = await cashierFetchPlayers({ search: search.trim() || undefined, limit: 20, page: 1 });
      setPlayers((res.data || []) as PlayerRow[]);
    } catch {
      setPlayers([]);
    } finally {
      setLoadingPlayers(false);
    }
  };

  useEffect(() => {
    void loadShift();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const t = setTimeout(() => void loadPlayers(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  if (!id) return <Navigate to="/" replace />;

  if (!loading && !shift) return <Navigate to="/" replace />;

  if (!loading && shift?.status === 'scheduled') {
    return <Navigate to={`/shifts/${id}/opening`} replace />;
  }

  const canOperate = !!shift && canJoinShift(shift.status);

  const shiftSubtitle = (() => {
    if (!shift) return undefined;
    if (canJoinShift(shift.status) && shift.openedAt) {
      return `Active since ${new Date(shift.openedAt).toLocaleString()}`;
    }
    if (shift.status) {
      return `Status: ${shift.status.replace(/_/g, ' ')}`;
    }
    return undefined;
  })();

  const doCreatePlayer = async () => {
    if (!newPhone.trim() && !newEmail.trim()) {
      toast.error('Phone or email is required');
      return;
    }
    setCreating(true);
    try {
      const res = await cashierCreatePlayer({
        phone: newPhone.trim() || undefined,
        email: newEmail.trim() || undefined,
      });
      if (!res.success) {
        toast.error(res.message || res.code || 'Create player failed');
        return;
      }
      toast.success('Player created');
      setCreateOpen(false);
      setNewPhone('');
      setNewEmail('');
      await loadPlayers();
    } catch (e: unknown) {
      toast.error((e as any)?.response?.data?.message || 'Create player failed');
    } finally {
      setCreating(false);
    }
  };

  const doDeposit = async (playerId: string) => {
    const v = Number(amount[playerId]);
    if (!Number.isFinite(v) || v <= 0) {
      toast.error('Amount must be > 0');
      return;
    }
    const res = await cashierDepositCredits(playerId, { amount: v });
    if (!res.success) {
      toast.error(res.message || res.code || 'Deposit failed');
      return;
    }
    toast.success('Deposit completed');
    setAmount((p) => ({ ...p, [playerId]: '' }));
    await Promise.all([loadPlayers(), loadShift()]);
  };

  const doRedeem = async (playerId: string) => {
    const v = Number(amount[playerId]);
    if (!Number.isFinite(v) || v <= 0) {
      toast.error('Amount must be > 0');
      return;
    }
    const res = await cashierRedeemCredits(playerId, { amount: v });
    if (!res.success) {
      toast.error(res.message || res.code || 'Redeem failed');
      return;
    }
    if (res.data?.pendingApproval) {
      toast('Redemption submitted for Shop Manager approval. Do not pay cash to the player.', {
        icon: '⏳',
        duration: 6000,
      });
      await loadShift();
    } else {
      toast.success('Redeem completed');
      await Promise.all([loadPlayers(), loadShift()]);
    }
    setAmount((p) => ({ ...p, [playerId]: '' }));
  };

  const pageTitle = shift && canJoinShift(shift.status) ? 'Active shift' : 'Shift';

  return (
    <CashierLayout title={pageTitle} subtitle={shiftSubtitle} backTo="/">
      {loading ? (
        <div className="text-sm text-stone-600">Loading…</div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          {!canOperate && shift ? (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              This shift is not open for deposits or redemptions
              {shift.status ? ` (${shift.status.replace(/_/g, ' ')})` : ''}.
              You can still look up and create players.
            </div>
          ) : null}
          <div className="mb-4 flex flex-wrap gap-2">
            {canOperate ? (
              <>
                <Link
                  to={`/shifts/${id}/cash-drop`}
                  className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm font-semibold hover:bg-stone-50"
                >
                  Cash drop
                </Link>
                <Link
                  to={`/shifts/${id}/closing`}
                  className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100"
                >
                  Close shift
                </Link>
                <Link
                  to={`/shifts/${id}/handover`}
                  className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm font-semibold hover:bg-stone-50"
                >
                  Handover
                </Link>
              </>
            ) : null}
          </div>

          <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-2">
            <div className="flex min-h-0 flex-col rounded-xl border border-stone-200 bg-white p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold text-stone-900">Player lookup</div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                    onClick={() => setCreateOpen(true)}
                  >
                    Create player
                  </button>
                  <button
                    type="button"
                    className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
                    onClick={() => void loadPlayers()}
                  >
                    Refresh
                  </button>
                </div>
              </div>
              <div className="mt-3">
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                  placeholder="Search by phone / username"
                />
              </div>
              {canOperate ? (
                <p className="mt-2 text-xs text-stone-500">
                  Redemptions above shop limits require manager approval. Do not pay cash until approved.
                </p>
              ) : null}
              <div className="mt-3 min-h-0 flex-1 space-y-3 overflow-y-auto">
                {loadingPlayers ? (
                  <div className="text-sm text-stone-600">Loading…</div>
                ) : players.length === 0 ? (
                  <div className="text-sm text-stone-600">No players found.</div>
                ) : (
                  players.map((p) => (
                    <div key={p.id} className="rounded-lg border border-stone-200 p-3">
                      <div className="truncate text-sm font-semibold text-stone-900">
                        {p.username || p.phone || p.id}
                      </div>
                      <div className="mt-3 grid gap-2 md:grid-cols-4">
                        <input
                          value={amount[p.id] || ''}
                          onChange={(e) => setAmount((prev) => ({ ...prev, [p.id]: e.target.value }))}
                          className="md:col-span-2 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                          placeholder="Amount"
                          disabled={!canOperate}
                        />
                        <button
                          type="button"
                          onClick={() => void doDeposit(p.id)}
                          className="w-full rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                          disabled={!canOperate}
                        >
                          Deposit
                        </button>
                        <button
                          type="button"
                          onClick={() => void doRedeem(p.id)}
                          className="w-full rounded-lg bg-stone-800 px-3 py-2 text-sm font-semibold text-white hover:bg-stone-900 disabled:opacity-60"
                          disabled={!canOperate}
                        >
                          Redeem
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="flex min-h-0 flex-col rounded-xl border border-stone-200 bg-white p-4">
              <div className="text-sm font-semibold text-stone-900">Shift transactions</div>
              {shift?.openingCashBalance != null ? (
                <div className="mt-1 text-xs text-stone-600">
                  Opening cash: {formatCurrency(Number(shift.openingCashBalance))}
                </div>
              ) : null}
              <div className="mt-3 min-h-0 flex-1 space-y-2 overflow-y-auto">
                {transactions.length === 0 ? (
                  <div className="text-sm text-stone-600">No transactions yet.</div>
                ) : (
                  transactions.map((tx) => {
                    const status = getTxStatus(tx);
                    const isPendingRedeem = tx.type === 'redeem_credits' && status === 'pending_approval';
                    return (
                      <div key={tx.id} className="flex items-center justify-between rounded-lg border border-stone-100 px-3 py-2 text-sm">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-stone-900">{formatTxType(tx.type)}</span>
                            <span
                              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${txStatusBadgeClass(status)}`}
                            >
                              {txStatusLabel(status)}
                            </span>
                          </div>
                          <div className="text-xs text-stone-500">
                            {tx.createdAt ? new Date(tx.createdAt).toLocaleString() : ''}
                          </div>
                          {isPendingRedeem ? (
                            <div className="mt-0.5 text-xs font-medium text-amber-700">
                              Do not pay cash until approved
                            </div>
                          ) : null}
                        </div>
                        <div className={`ml-3 shrink-0 font-semibold ${txAmountClass(tx.type, status)}`}>
                          {tx.amount != null ? formatCurrency(Number(tx.amount)) : '—'}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {createOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <div className="text-lg font-semibold">Create player</div>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-sm font-medium text-stone-700">Phone</label>
                <div className="mt-1 transfer-phone-field">
                  <PhoneInput
                    value={newPhone}
                    onChange={setNewPhone}
                    defaultCountry="US"
                    placeholder="Phone number"
                    disabled={creating}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Email</label>
                <input
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                  placeholder="player@example.com"
                />
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
                onClick={() => setCreateOpen(false)}
                disabled={creating}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                onClick={() => void doCreatePlayer()}
                disabled={creating}
              >
                {creating ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </CashierLayout>
  );
}
